import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import { Clock, Target, AlertCircle } from 'lucide-react';

interface LiveMatch {
  id: string;
  match_name: string;
  home_team: string;
  away_team: string;
  home_score: number;
  away_score: number;
  status: string;
  minute: number;
  competition: string;
  match_date: string;
}

interface MatchEvent {
  id: string;
  event_type: string;
  minute: number;
  player_name: string;
  description: string;
  team: string;
}

export const LiveMatch = () => {
  const [liveMatch, setLiveMatch] = useState<LiveMatch | null>(null);
  const [events, setEvents] = useState<MatchEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLiveMatch = async () => {
    const { data, error } = await supabase
      .from('live_matches')
      .select('*')
      .eq('status', 'live')
      .order('match_date', { ascending: true })
      .limit(1)
      .maybeSingle();

    if (!error && data) {
      setLiveMatch(data);
      fetchEvents(data.id);
    }
    setLoading(false);
  };

  const fetchEvents = async (matchId: string) => {
    const { data, error } = await supabase
      .from('match_events')
      .select('*')
      .eq('match_id', matchId)
      .order('minute', { ascending: false });

    if (!error && data) {
      setEvents(data);
    }
  };

  useEffect(() => {
    fetchLiveMatch();
  }, []);

  useRealtimeSubscription({
    table: 'live_matches',
    event: 'UPDATE',
    callback: (payload) => {
      if (payload.new.status === 'live') {
        setLiveMatch(payload.new);
      } else if (payload.new.status === 'finished') {
        setLiveMatch(null);
      }
    },
  });

  useRealtimeSubscription({
    table: 'match_events',
    event: 'INSERT',
    callback: (payload) => {
      if (liveMatch && payload.new.match_id === liveMatch.id) {
        setEvents((prev) => [payload.new, ...prev]);
      }
    },
  });

  if (loading) {
    return (
      <Card className="p-6 bg-gradient-to-br from-dark-red/10 to-golden-yellow/5">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-3/4"></div>
          <div className="h-20 bg-muted rounded"></div>
        </div>
      </Card>
    );
  }

  if (!liveMatch) {
    return (
      <Card className="p-6 bg-card border-border">
        <div className="text-center text-muted-foreground">
          <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-lg font-semibold">Kein Live-Spiel gerade</p>
          <p className="text-sm">Schau später wieder vorbei!</p>
        </div>
      </Card>
    );
  }

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'goal':
        return '⚽';
      case 'yellow_card':
        return '🟨';
      case 'red_card':
        return '🟥';
      case 'substitution':
        return '🔄';
      case 'penalty':
        return '⚽';
      case 'var':
        return '📺';
      default:
        return '•';
    }
  };

  return (
    <Card className="p-8 bg-dark-red border-4 border-golden-yellow rounded-3xl shadow-2xl">
      <div className="flex items-center justify-between gap-8">
        <div className="flex-1">
          <div className="text-sm font-bold text-golden-yellow/70 mb-2">
            <span className="font-black">{new Date(liveMatch.match_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
          </div>
          <div className="text-sm font-bold text-golden-yellow/70 mb-4">
            <span className="font-black">{liveMatch.competition}</span>
          </div>
          
          <div className="flex items-center gap-4 bg-red-600 px-8 py-4 rounded-2xl animate-pulse">
            <div className="text-5xl font-black text-white">LIVE</div>
          </div>
        </div>
        
        <div className="flex-1 flex flex-col items-center justify-center gap-4">
          <div className="flex items-center justify-center gap-8 w-full">
            <div className="text-right flex-1">
              <div className="text-2xl font-black text-golden-yellow">{liveMatch.home_team}</div>
            </div>
            
            <div className="flex items-center gap-4 bg-golden-yellow px-8 py-4 rounded-2xl">
              <div className="text-5xl font-black text-dark-red">{liveMatch.home_score}</div>
              <div className="text-3xl font-black text-dark-red">:</div>
              <div className="text-5xl font-black text-dark-red">{liveMatch.away_score}</div>
            </div>
            
            <div className="text-left flex-1">
              <div className="text-2xl font-black text-golden-yellow">{liveMatch.away_team}</div>
            </div>
          </div>
        </div>
        
        <div className="flex-1"></div>
      </div>

      {events.length > 0 && (
        <div className="mt-8 pt-8 border-t-2 border-golden-yellow">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-5 h-5 text-golden-yellow" />
            <h3 className="font-black text-golden-yellow text-xl uppercase">Live-Events</h3>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {events.map((event) => (
              <div
                key={event.id}
                className="flex items-center gap-3 p-4 bg-golden-yellow rounded-xl border-2 border-cream"
              >
                <span className="text-2xl">{getEventIcon(event.event_type)}</span>
                <div className="flex-1">
                  <p className="font-black text-dark-red text-sm">{event.player_name}</p>
                  <p className="text-xs text-warm-brown font-bold">{event.description}</p>
                </div>
                <span className="font-mono text-sm font-black text-dark-red">{event.minute}'</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};
